// core
import React, { useContext, useState } from "react";
import { useFormikContext } from "formik";

// hooks
import useSettingsValidation from "../../hooks/useSettingsValidation";
import { QuoteGroupContext } from "../../providers/RootContextProvider";

// components
import Input from "../../../common/components/Forms/Input/Input";
import Icon from "../../../common/components/Icon/Icon";
import StreamlineBaseClientSettings, {
    IStreamlineSettingsClientProps
} from "./StreamlineBaseClientSettings";
import LifeCalculatorModal from "../Modals/LifeCalculatorModal/LifeCalculatorModal";
import LifeCompareCoverAmountsModal from "../Modals/LifeCompareCoverAmountsModal/LifeCompareCoverAmountsModal";

// interfaces
import { IQuoteSettings } from "../../interfaces/form/IQuoteSettings";
import { IClient } from "../../../common/interfaces/IClient";
import IBarGraphData from "../../interfaces/IBarGraphData";
import ProductType from "../../../common/interfaces/enums/ProductType";
import { SmokerStatus } from "../../../common/interfaces/enums/SmokerStatus";
import { Gender } from "../../../common/interfaces/enums/Gender";

// styles
import cx from "classnames";

const StreamlineLifeSettings: React.FC<IStreamlineSettingsClientProps> = (props) => {
    const { client, name } = props;
    const { handleBlur, setFieldValue } = useFormikContext<IQuoteSettings>();
    const { getFormikError } = useSettingsValidation();

    const { selectors } = useContext(QuoteGroupContext);

    const [isOpenLifeCalculatorModal, setIsOpenLifeCalculatorModal] = useState(false);
    const [isOpenCompareCoverAmounts, setIsOpenCompareCoverAmounts] = useState(false);

    const handleClickLifeCalculator = (): void => {
        setIsOpenLifeCalculatorModal(true);
    };

    const canCompareCoverAmounts = selectors
        .getClients()
        .every(
            (client) =>
                client.gender !== Gender.Unknown &&
                client.smokerStatus !== SmokerStatus.Unknown
        );

    const getGraphData = (clients: IClient[]): IBarGraphData[] =>
        clients
            .filter((client) => !client.isChild)
            .map((client) => {
                return {
                    age: client.age,
                    isSmoker: client.smokerStatus === SmokerStatus.Smoker,
                    gender: client.gender
                };
            });

    return (
        <React.Fragment>
            <Input
                name={`${name}.${ProductType.Life}.coverAmount`}
                label={`Cover amount`}
                type={`number`}
                value={client[ProductType.Life]?.coverAmount}
                hasCurrency
                onBlur={handleBlur}
                className="gap-m"
                onChange={(e) => {
                    setFieldValue(`clients.${props.index}.${ProductType.Life}.coverAmount`, e)
                }}
                hasError={getFormikError(`${name}.${ProductType.Life}.coverAmount`)}>
                <p className="text-font-size-s gap-pre-s">
                    <Icon
                        id="Calculator"
                        size="xl"
                        className={cx("calculator-icon-position")}
                    />
                    Not sure?&nbsp;
                    <a onClick={handleClickLifeCalculator} className="btn-link">Use our calculator</a>
                </p>
            </Input>
            <LifeCalculatorModal
                isOpen={isOpenLifeCalculatorModal}
                onClose={(coverAmount?: number) => {
                    setIsOpenLifeCalculatorModal(false);
                    if (coverAmount && coverAmount > 0) {
                        setFieldValue(
                            `clients.${props.index}.${ProductType.Life}.coverAmount`,
                            coverAmount ||
                            500000
                        );
                    }
                }}
            />
            <span className="layout-flex-row--centered gap-pre-l">
                <Icon id="HowMuchDoOthers" className="space-m" />
                <a
                    className={cx({
                        "layout-flex-column text-font-size-s": true,
                        "disabled": !canCompareCoverAmounts
                    })}
                    onClick={(e) => {
                        if (canCompareCoverAmounts) setIsOpenCompareCoverAmounts(true);
                        else e.preventDefault;
                    }}>
                    <span>How much do other</span>
                    <span className="text-line-height-s">people apply for?</span>
                </a>
            </span>
            <LifeCompareCoverAmountsModal
                isOpen={isOpenCompareCoverAmounts}
                onClose={() => setIsOpenCompareCoverAmounts(false)}
                clientSettings={getGraphData(selectors.getClients())}
            />
        </React.Fragment>
    );
};

export default StreamlineLifeSettings;
